import './dashboardPage.css';
import React, {Component} from "react";
import UserDetailsService from "../services/userDetailsService";
import {Navigate} from "react-router-dom";
import RedirectionService from "../services/redirectionService";
import {TransactionSummaryDto, UserDto, BasicUserDto} from "../model/functionModels";
import {ToastErrorLevel} from "../../App";
import {withRouter} from "./withRouter";
import UserDisplay from "./userDisplay";
import DebtMap from "./debtMap";
import AddExpense from "./addExpense";
import AddPayment from "./addPayment";
import SummaryService from "../services/summaryService";
import TransactionService from "../services/transactionService";
import PaymentModal from "./paymentModal";
import PaymentMethodButton from "./paymentMethodButton";
import PaymentConfirmationModal from "./paymentConfirmationModal";
import LoadingSpinner from "./loadingSpinner";
import i18nService from "../../i18n/i18nService";

interface DashboardState {
    navigation?: string,
    loggedInUser?: UserDto,
    allUsers?: (UserDto | BasicUserDto)[],
    summary?: TransactionSummaryDto,
    isLoading: boolean,
    payingFriendId?: string,
    showPaymentModal?: {
        friendId: string,
        friendName: string,
        totalDebt: number,
    },
    showPaymentConfirmationModal?: {
        friendId: string,
        friendName: string,
        totalDebt: number,
    },
}

interface DashboardProps {
    toastGenerator: (body: string, level: ToastErrorLevel) => any,
    onLogout: any,
}

class DashboardPage extends Component<DashboardProps, DashboardState> {
    private unsubscribe?: () => void;

    constructor(props: DashboardProps) {
        super(props, {})
        this.state = {
            isLoading: false,
        };
        this.handleFailure = this.handleFailure.bind(this);
        this.handleRedirection = this.handleRedirection.bind(this);
        this.refreshAll = this.refreshAll.bind(this);
        this.handlePayment = this.handlePayment.bind(this);
        this.handlePaymentClick = this.handlePaymentClick.bind(this);
        this.handlePaymentCancel = this.handlePaymentCancel.bind(this);
        this.handlePaymentConfirm = this.handlePaymentConfirm.bind(this);
        this.handlePaymentMethodComplete = this.handlePaymentMethodComplete.bind(this);
        this.handlePaymentConfirmationCancel = this.handlePaymentConfirmationCancel.bind(this);
        this.handlePaymentConfirmationConfirm = this.handlePaymentConfirmationConfirm.bind(this);
    }

    async componentDidMount() {
        // Subscribe to locale changes to trigger re-render
        this.unsubscribe = i18nService.subscribe(() => {
            this.forceUpdate();
        });
        
        if (!this.state.navigation) {
            await this.refreshAll();
        }
    }

    componentWillUnmount() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    }

    async refreshAll() {
        // Parallelize API calls for better performance
        const promises = [
            UserDetailsService.getOwnUserDetails(async userDto => {
                this.setState({
                    loggedInUser: userDto,
                });
            }, async () => {
                // @ts-ignore
                const currentPath = this.props.location?.pathname || '/dashboard';
                this.setState({
                    navigation: RedirectionService.redirectToLogin(currentPath),
                })
            },
            this.handleRedirection),
            UserDetailsService.getAllUsersList(async users => {
                this.setState({
                    allUsers: users,
                })
            }, this.handleFailure, this.handleRedirection),
            SummaryService.getMySummary(async summary => {
                this.setState({
                    summary: summary,
                });
            }, this.handleFailure, this.handleRedirection)
        ];
        
        // Wait for all API calls to complete in parallel
        await Promise.all(promises);
    }

    async handleRedirection(redirectionUrl: string) {
        if (redirectionUrl.startsWith("/login")) {
            this.props.onLogout();
        }
        this.setState({
            navigation: redirectionUrl
        });
    }

    async handleFailure(error: string) {
        this.props.toastGenerator(error, ToastErrorLevel.ERROR);
    }

    handlePaymentClick(friendId: string, isFullPayment: boolean) {
        if (!this.state.loggedInUser || !this.state.summary) {
            return;
        }
        if (this.state.isLoading || this.state.payingFriendId) {
            return;
        }

        const simplifiedDebts = this.state.summary.simplifiedDebts;
        // debtMap[loggedInUser][friendId] means "loggedInUser owes friendId"
        const debtToFriend = simplifiedDebts[this.state.loggedInUser.id]?.[friendId] || 0;
        
        if (debtToFriend <= 0) {
            this.props.toastGenerator("You don't owe this friend anything!", ToastErrorLevel.INFO);
            return;
        }

        // Ensure we only pay the debt for this specific friend (amounts are in cents)
        const paymentAmount = Math.round(Math.abs(debtToFriend));
        
        if (isFullPayment) {
            // Directly process full payment for this specific friend only
            this.handlePayment(friendId, paymentAmount, true);
        } else {
            // Show modal for partial payment
            const friend = this.getFriendDtos().find(f => f.id === friendId);
            const friendName = friend ? `${friend.firstName} ${friend.lastName}` : "Friend";
            this.setState({
                showPaymentModal: {
                    friendId,
                    friendName,
                    totalDebt: paymentAmount,
                }
            });
        }
    }

    handlePaymentCancel() {
        this.setState({showPaymentModal: undefined});
    }

    async handlePaymentConfirm(amount: number) {
        if (!this.state.showPaymentModal) {
            return;
        }
        if (this.state.isLoading || this.state.payingFriendId) {
            return;
        }
        this.setState({showPaymentModal: undefined, isLoading: true, payingFriendId: this.state.showPaymentModal.friendId});
        await this.handlePayment(this.state.showPaymentModal.friendId, amount, false);
        this.setState({isLoading: false, payingFriendId: undefined});
    }

    handlePaymentMethodComplete(amount: number, isFull: boolean) {
        // This is called from PaymentMethodButton with friendId from closure
        // We need to find which friend triggered this
        // Actually, we should pass friendId through the callback
        // For now, let's use a different approach - store the friendId in state when button is clicked
    }

    handlePaymentMethodButtonClick(friendId: string) {
        if (!this.state.loggedInUser || !this.state.summary) {
            return;
        }
        const friend = this.getFriendDtos().find(f => f.id === friendId);
        const friendName = friend ? `${friend.firstName} ${friend.lastName}` : "Friend";
        const simplifiedDebts = this.state.summary.simplifiedDebts;
        const debtToFriend = simplifiedDebts[this.state.loggedInUser.id]?.[friendId] || 0;
        const totalDebt = Math.round(Math.abs(debtToFriend));
        
        this.setState({
            showPaymentConfirmationModal: {
                friendId,
                friendName,
                totalDebt,
            }
        });
    }

    handlePaymentConfirmationCancel() {
        this.setState({showPaymentConfirmationModal: undefined});
    }

    async handlePaymentConfirmationConfirm(amount: number, isFull: boolean) {
        if (!this.state.showPaymentConfirmationModal) {
            return;
        }
        this.setState({showPaymentConfirmationModal: undefined});
        await this.handlePayment(this.state.showPaymentConfirmationModal.friendId, amount, isFull);
    }

    async handlePayment(friendId: string, amount: number, isFullPayment: boolean) {
        if (!this.state.loggedInUser) {
            return;
        }

        if (amount <= 0) {
            this.props.toastGenerator("Payment amount must be greater than 0", ToastErrorLevel.ERROR);
            return;
        }

        if (this.state.isLoading || this.state.payingFriendId) {
            return;
        }
        this.setState({isLoading: true, payingFriendId: friendId});
        const today = new Date().toISOString().split('T')[0];
        await TransactionService.createPayment({
            authorUserId: this.state.loggedInUser.id,
            date: today,
            amount: amount, // Already in cents
            userPaidTo: friendId,
        }, async () => {
            const amountInEuros = (amount / 100).toFixed(2);
            this.props.toastGenerator(`Payment of €${amountInEuros} recorded!`, ToastErrorLevel.INFO);
            await this.refreshAll();
        }, this.handleFailure, this.handleRedirection);
        this.setState({isLoading: false, payingFriendId: undefined});
    }

    getFriendDtos(): (UserDto | BasicUserDto)[] {
        if (!this.state.loggedInUser || !this.state.allUsers) {
            return [];
        }
        return this.state.allUsers.filter(u => u.id !== this.state.loggedInUser!.id && this.state.loggedInUser!.friends.includes(u.id));
    }

    getDebtsToFriends(): Array<{friend: UserDto | BasicUserDto, amount: number}> {
        if (!this.state.loggedInUser || !this.state.summary) {
            return [];
        }
        const simplifiedDebts = this.state.summary.simplifiedDebts;
        const friends = this.getFriendDtos();
        // debtMap[loggedInUser][friend] means "loggedInUser owes friend"
        return friends.map(friend => {
            const debt = simplifiedDebts[this.state.loggedInUser!.id]?.[friend.id] || 0;
            return {friend, amount: debt};
        }).filter(d => d.amount > 0).sort((a, b) => b.amount - a.amount);
    }

    getDebtsFromFriends(): Array<{friend: UserDto | BasicUserDto, amount: number}> {
        if (!this.state.loggedInUser || !this.state.summary) {
            return [];
        }
        const simplifiedDebts = this.state.summary.simplifiedDebts;
        const friends = this.getFriendDtos();
        // debtMap[friend][loggedInUser] means "friend owes loggedInUser"
        return friends.map(friend => {
            const debt = simplifiedDebts[friend.id]?.[this.state.loggedInUser!.id] || 0;
            return {friend, amount: debt};
        }).filter(d => d.amount > 0).sort((a, b) => b.amount - a.amount);
    }

    render() {
        if (this.state.navigation) {
            return <Navigate to={this.state.navigation}/>
        }
        if (!this.state.loggedInUser || !this.state.summary || !this.state.allUsers) {
            return <div className="DashboardPage-Loading">
                <LoadingSpinner size="large" message={i18nService.t('common.loading')} />
            </div>
        }
        const friends = this.getFriendDtos();
        const involvedUsers = [this.state.loggedInUser, ...friends];
        const debtsToFriends = this.getDebtsToFriends();
        const debtsFromFriends = this.getDebtsFromFriends();

        return <div className="DashboardPage-Wrapper">
            <div className="DashboardPage-Container">
                <h1 className="DashboardPage-Title">{i18nService.t('dashboard.title')}</h1>

                <div className="DashboardPage-Section">
                    <h2 className="DashboardPage-SectionTitle">{i18nService.t('dashboard.youOwe')}</h2>
                    {debtsToFriends.length === 0 ? (
                        <p className="DashboardPage-EmptyState">{i18nService.t('dashboard.youOweNothing')}</p>
                    ) : (
                        <div className="DashboardPage-DebtsList">
                            {debtsToFriends.map(({friend, amount}) => (
                                <div key={`owe-${friend.id}`} className="DashboardPage-DebtItem">
                                    <div className="DashboardPage-DebtInfo">
                                        <UserDisplay user={friend} showName={true} abbreviateName={false}/>
                                        <div className="DashboardPage-DebtAmount">
                                            €{(Math.abs(amount) / 100).toFixed(2)}
                                        </div>
                                    </div>
                                    <div className="DashboardPage-DebtActions">
                                        <PaymentMethodButton
                                            targetUserId={friend.id}
                                            totalDebt={Math.abs(amount)}
                                            onPaymentComplete={() => this.handlePaymentMethodButtonClick(friend.id)}
                                            disabled={this.state.isLoading || this.state.payingFriendId === friend.id}
                                        />
                                                <button
                                                    className="DashboardPage-Button DashboardPage-ButtonSecondary"
                                                    onClick={() => this.handlePaymentClick(friend.id, false)}
                                                    disabled={this.state.isLoading || this.state.payingFriendId === friend.id}
                                                >
                                                    {this.state.payingFriendId === friend.id ? i18nService.t('dashboard.paying') : i18nService.t('dashboard.payPartial')}
                                                </button>
                                                <button
                                                    className="DashboardPage-Button DashboardPage-ButtonPrimary"
                                                    onClick={() => this.handlePaymentClick(friend.id, true)}
                                                    disabled={this.state.isLoading || this.state.payingFriendId === friend.id}
                                                >
                                                    {this.state.payingFriendId === friend.id ? i18nService.t('dashboard.paying') : i18nService.t('dashboard.payFull')}
                                                </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="DashboardPage-Section">
                    <h2 className="DashboardPage-SectionTitle">{i18nService.t('dashboard.youAreOwed')}</h2>
                    {debtsFromFriends.length === 0 ? (
                        <p className="DashboardPage-EmptyState">{i18nService.t('dashboard.noOneOwes')}</p>
                    ) : (
                        <div className="DashboardPage-DebtsList">
                            {debtsFromFriends.map(({friend, amount}) => (
                                <div key={`owed-${friend.id}`} className="DashboardPage-DebtItem DashboardPage-DebtItemOwed">
                                    <div className="DashboardPage-DebtInfo">
                                        <UserDisplay user={friend} showName={true} abbreviateName={false}/>
                                        <div className="DashboardPage-DebtAmount DashboardPage-DebtAmountOwed">
                                            €{(Math.abs(amount) / 100).toFixed(2)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="DashboardPage-Section">
                    <h2 className="DashboardPage-SectionTitle">{i18nService.t('dashboard.addExpense')}</h2>
                    <AddExpense loggedInUser={this.state.loggedInUser}
                                possibleUsers={involvedUsers}
                                onFailure={this.handleFailure}
                                onRedirection={this.handleRedirection}
                                refreshPageContent={async () => {
                                    await this.refreshAll();
                                }}/>
                </div>

                <div className="DashboardPage-Section">
                    <h2 className="DashboardPage-SectionTitle">{i18nService.t('dashboard.addPayment')}</h2>
                    <AddPayment loggedInUser={this.state.loggedInUser}
                                possibleUsers={friends}
                                onFailure={this.handleFailure}
                                onRedirection={this.handleRedirection}
                                refreshPageContent={async () => {
                                    await this.refreshAll();
                                }}/>
                </div>
            </div>
            {this.state.showPaymentModal && (
                <PaymentModal
                    friendName={this.state.showPaymentModal.friendName}
                    totalDebt={this.state.showPaymentModal.totalDebt}
                    onConfirm={this.handlePaymentConfirm}
                    onCancel={this.handlePaymentCancel}
                />
            )}
            {this.state.showPaymentConfirmationModal && (
                <PaymentConfirmationModal
                    friendName={this.state.showPaymentConfirmationModal.friendName}
                    totalDebt={this.state.showPaymentConfirmationModal.totalDebt}
                    onConfirm={this.handlePaymentConfirmationConfirm}
                    onCancel={this.handlePaymentConfirmationCancel}
                />
            )}
        </div>
    }
}

export default withRouter(DashboardPage);

